import React from 'react'
import { useIntl } from 'react-intl'
import { useConfig } from 'msp-integrations'
import { XIcon } from '@heroicons/react/solid'
import LineItemPromotionLabel from './LineItemPromotionLabel'
import { removeCoupon } from './LineItemPromotion.helpers'
import type {
  LineItemPromotionProps,
  BonusDiscountLineItem
} from './LineItemPromotion.types'

const LineItemPromotion = ({
  priceAdjustments,
  currency,
  removePromoCode,
  handleBonusDiscountLineItem = () => {},
  couponItems,
  bonusItemsByCouponCode = new Map<string, BonusDiscountLineItem>(),
  headline,
  summary = false,
  inline = false,
  readOnly = false,
  isFriendCoupon = false
}: LineItemPromotionProps) => {
  const { formatMessage } = useIntl()
  // @ToDo tenantId check should be removed after GiftCard refactoring, using ProductLineItems instead of GiftCertLineItems
  const {
    theme: { font },
    tenantId
  } = useConfig()

  return (
    <>
      {headline && (
        <div
          className={`bg-promotionBg mb-1 w-full px-4 py-2 font-medium ${font.regular}`}
        >
          {headline}
        </div>
      )}
      {summary &&
        couponItems.map((cI) => (
          <div
            key={cI.couponItemId}
            className={`bg-promotionBg ${
              font.regular
            } relative mb-1 flex w-full items-center justify-between space-x-6 py-2 pl-4 pr-4 uppercase last:mb-0 ${
              inline ? 'md:pl-[150px]' : ''
            }`}
          >
            <div className='flex flex-row md:px-8'>
              <div className='inline-block'>{cI.code || 'Promo'} -</div>
              {!bonusItemsByCouponCode.has(cI.code) &&
                !cI.code.startsWith('WO-') &&
                tenantId === 'jasmin' &&
                !isFriendCoupon && (
                  <div className='ml-1 capitalize'>
                    {formatMessage({
                      id: 'msp.cart.order.promotion.not.valid.label',
                      defaultMessage: '(Not applicable)'
                    })}
                  </div>
                )}
              {((!bonusItemsByCouponCode.has(cI.code) &&
                cI.code.startsWith('WO-')) ||
                isFriendCoupon) && (
                <div className='ml-1 capitalize'>
                  {formatMessage({
                    id: 'msp.cart.order.promotion.wo.valid.label',
                    defaultMessage: '(Applied)'
                  })}
                </div>
              )}
              {bonusItemsByCouponCode.has(cI.code) && (
                <div className='ml-1'>
                  <a
                    href='#'
                    onClick={(event: any) => {
                      event.preventDefault()
                      handleBonusDiscountLineItem(
                        bonusItemsByCouponCode.get(cI.code)
                      )
                    }}
                    className='custom-link custom-link--standard custom-link--primary inline-block h-fit uppercase'
                  >
                    {formatMessage({
                      id: 'msp.cart.order.promotion.select.bonus.products.label',
                      defaultMessage: 'Click here to select'
                    })}
                  </a>
                </div>
              )}
            </div>
            {!readOnly ? (
              <span
                className='ml-5 w-5 cursor-pointer'
                onClick={() =>
                  removeCoupon(cI.code, removePromoCode, couponItems)
                }
              >
                <XIcon className='h-5 w-5' />
              </span>
            ) : (
              <span className='ml-5 inline-flex w-5' />
            )}
          </div>
        ))}
      {!summary &&
        (
          priceAdjustments?.filter(
            (adjustment) =>
              !adjustment?.promotionId?.startsWith('CustomerCredit-')
          ) || []
        ).map((pA: any) => (
          <div
            key={pA.priceAdjustmentId}
            className={`bg-promotionBg ${
              font.regular
            } relative mb-1 flex w-full items-center justify-between space-x-6 py-2 pl-4 pr-4 uppercase last:mb-0 ${
              inline ? 'md:pl-[150px]' : ''
            }`}
          >
            <div className='flex flex-row md:px-8'>
              {pA.couponCode && bonusItemsByCouponCode.has(pA.couponCode) ? (
                <>
                  <div className='inline-block'>
                    {pA.couponCode || pA.itemText || 'Promo'} -
                  </div>
                  <div className='ml-1'>
                    <a
                      href='#'
                      onClick={(event: any) => {
                        event.preventDefault()
                        handleBonusDiscountLineItem(
                          bonusItemsByCouponCode.get(pA.couponCode)
                        )
                      }}
                      className='custom-link custom-link--standard custom-link--primary inline-block h-fit uppercase'
                    >
                      {formatMessage({
                        id: 'msp.cart.order.promotion.edit.bonus.products.label',
                        defaultMessage: 'Click here to edit'
                      })}
                    </a>
                  </div>
                </>
              ) : (
                <>{pA.couponCode || pA.itemText || 'Promo'}</>
              )}
            </div>
            <div>
              <LineItemPromotionLabel
                priceAdjustment={pA}
                currency={currency}
              />
              {pA.couponCode && !readOnly ? (
                <span
                  className='ml-5 w-5 cursor-pointer'
                  onClick={() =>
                    removeCoupon(pA.couponCode, removePromoCode, couponItems)
                  }
                >
                  <XIcon className='h-5 w-5' />
                </span>
              ) : (
                <span className='ml-5 inline-flex w-5' />
              )}
            </div>
          </div>
        ))}
    </>
  )
}

export default LineItemPromotion
